import React, { useEffect, useState, useRef } from "react";
import styled from "styled-components";
import { useParams, useNavigate } from "react-router-dom";
import TopNavbar from "../components/Nav/TopNavbar";
import FullButton from "../components/Buttons/FullButton";
import ServiceAPI from "../services/NavaService";
import Footer from "../components/Sections/Footer"

import AddImage1 from "../assets/img/add/1.png";
import AddImage2 from "../assets/img/add/2.png";
import AddImage3 from "../assets/img/add/3.png";
import AddImage4 from "../assets/img/add/4.png";

export default function ServicesDesc() {
  const { srvId } = useParams();
  const navigate = useNavigate();

  const [service, setService] = useState(null);
  const [images, setImages] = useState([]);
  const createdUrlsRef = useRef([]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, []);

  useEffect(() => {
    if (!srvId) return;
    loadService();
    return () => {
      createdUrlsRef.current.forEach((u) => {
        try { URL.revokeObjectURL(u); } catch (e) { }
      });
      createdUrlsRef.current = [];
    };
  }, [srvId]);

  const loadService = async () => {
    try {
      const res = await ServiceAPI.getServiceById(srvId);
      const svc = res.data || null;
      setService(svc);

      const listRes = await ServiceAPI.listImages(srvId);
      const meta = listRes.data || [];

      const normalizedMeta = meta.map((m) => ({
        ...m,
        imgId: m?.imgId ?? m?.id ?? m?.img_id ?? m?.nsi_id ?? null,
        createdAt: m?.createdAt ?? m?.created_at ?? m?.createdOn ?? null,
        filename: m?.filename ?? m?.name ?? m?.fileName ?? "image"
      })).sort((a, b) => {
        const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return tb - ta;
      });

      const firstFour = normalizedMeta.slice(0, 4);
      const withPreviews = await Promise.all(
        firstFour.map(async (m) => {
          if (!m.imgId) return { ...m, previewUrl: null };
          try {
            const blobResp = await ServiceAPI.getImage(srvId, m.imgId);
            const url = URL.createObjectURL(blobResp.data);
            createdUrlsRef.current.push(url);
            return { ...m, previewUrl: url };
          } catch (err) {
            console.warn("Failed to load image blob for preview", m, err);
            return { ...m, previewUrl: null };
          }
        })
      );

      setImages(withPreviews);
    } catch (err) {
      console.error("Failed to load service or images", err);
    }
  };

  const getTags = (svc) => {
    if (!svc) return [];
    const raw = svc.tags ?? svc.tag ?? svc.tagsJson ?? null;
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    if (typeof raw === "string") {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return parsed;
      } catch (e) {
        return raw.split(",").map((t) => t.trim()).filter(Boolean);
      }
    }
    return [];
  };

  const placeholderImgs = [AddImage1, AddImage2, AddImage3, AddImage4];

  return (
    <>
      <TopNavbar />
      <PageWrapper style={{ marginTop: "5%" }}>
        <Container>
          <LightBg>
            <InnerContainer className="container">
              <Advertising className="flexSpaceCenter">
                <AddLeft>
                  <h4 className="font15 semiBold">A few words about the service</h4>
                  <h2 className="font40 extraBold">{service?.name ?? service?.service_name ?? "Service"}</h2>

                  <TagsRow>
                    {getTags(service).map((t) => (
                      <TagPill key={t}>{t}</TagPill>
                    ))}
                  </TagsRow>

                  <Description
                    className="font12"
                    dangerouslySetInnerHTML={{
                      __html: service?.description ?? service?.service_desc ?? "<p>No description available.</p>"
                    }}
                  />
                </AddLeft>

                <AddRight>
                  <AddRightInner>
                    <AddImgWrapp1>
                      <img src={images[0]?.previewUrl ?? placeholderImgs[0]} alt="main" />
                    </AddImgWrapp1>

                    <AddImgWrapp2>
                      <img src={images[1]?.previewUrl ?? placeholderImgs[1]} alt="side-1" />
                    </AddImgWrapp2>

                    <AddImgWrapp3>
                      <img src={images[2]?.previewUrl ?? placeholderImgs[2]} alt="side-2" />
                    </AddImgWrapp3>

                    <AddImgWrapp4>
                      <img src={images[3]?.previewUrl ?? placeholderImgs[3]} alt="side-3" />
                    </AddImgWrapp4>
                  </AddRightInner>
                </AddRight>
              </Advertising>
            </InnerContainer>
          </LightBg>
        </Container>
      </PageWrapper>
      <Footer />
    </>
  );
}

const PageWrapper = styled.div`
  padding: 24px;
  max-width: 1100px;
  margin: 0 auto;
  font-family: Arial, Helvetica, sans-serif;
`;

const Container = styled.div``;

const LightBg = styled.div`
  background: #f9fafb;
`;

const InnerContainer = styled.div`
  .container { padding: 0 12px; }
`;

const Advertising = styled.div`
  padding: 40px 0;
  margin: 40px 0;
  display: flex;
  align-items: center;
  justify-content: space-between;

  @media (max-width: 860px) {
    flex-direction: column;
    padding: 0 0 30px 0;
    margin: 20px 0 0px 0;
  }
`;

const AddLeft = styled.div`
  width: 50%;
  @media (max-width: 860px) {
    width: 100%;
    order: 2;
  }
`;

const AddRight = styled.div`
  width: 50%;
  @media (max-width: 860px) {
    width: 100%;
    order: 1;
    margin-bottom: 20px;
  }
`;

const ButtonsRow = styled.div`
  display:flex;
  gap: 12px;
  margin-top: 18px;
`;

const TagsRow = styled.div`
  display:flex;
  gap:8px;
  margin: 12px 0;
  flex-wrap:wrap;
`;

const TagPill = styled.div`
  background: #efefff;
  padding: 6px 10px;
  border-radius: 18px;
  font-size: 13px;
`;

const Description = styled.div`
  margin-top: 12px;
  line-height: 1.6;
  color: #333;
  img { max-width: 100%; height: auto; }
`;

const AddRightInner = styled.div`
  width: 100%;

  display: grid;
  grid-template-columns: 2fr 1fr;
  column-gap: 4%;       
  grid-template-rows: 220px 220px auto;
  gap: 16px;
  align-items: start;

  @media (max-width: 1160px) {
    grid-template-rows: 190px 190px auto;
  }

  @media (max-width: 860px) {
    padding-top: 80px; 
    box-sizing: border-box;

    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-template-rows: 280px auto auto;
    gap: 12px;
  }
`;

const AddImgWrapp1 = styled.div`
  overflow: hidden;
  border-radius: 8px;

  grid-column: 1 / 2; 
  grid-row: 1 / 3;    
  height: 440px;     
  img { width: 100%; height: 100%; object-fit: cover; display:block; }

  @media (max-width: 1160px) {
    height: 380px;
  }

  @media (max-width: 860px) {
    width: 100%;
    height: 280px;
    grid-column: 1 / 2;
    grid-row: 1 / 2;
  }
`;

const AddImgWrapp2 = styled.div`
  overflow: hidden;
  border-radius: 8px;

  grid-column: 2 / 3;  
  grid-row: 1 / 2;    
  height: 220px;     
  img { width: 100%; height: 100%; object-fit: cover; display:block; }

  @media (max-width: 1160px) {
    height: 190px;
  }

  @media (max-width: 860px) {
    width: 100%;
    height: 280px;
    grid-column: 2 / 3;
    grid-row: 1 / 2;
  }
`;

const AddImgWrapp3 = styled.div`
  overflow: hidden;
  border-radius: 8px;

  grid-column: 2 / 3; 
  grid-row: 2 / 3;     
  height: 220px;       
  img { width: 100%; height: 100%; object-fit: cover; display:block; }

  @media (max-width: 1160px) {
    height: 190px;
  }

  @media (max-width: 860px) {
    width: 100%;
    height: 160px;
    grid-column: 1 / 3;  
    grid-row: 2 / 3;
  }
`;

const AddImgWrapp4 = styled.div`
  overflow: hidden;
  border-radius: 8px;

  grid-column: 1 / 2;  
  grid-row: 3 / 4;   
  height: 220px;       
  img { width: 100%; height: 100%; object-fit: cover; display:block; }

  @media (max-width: 1160px) {
    height: 190px;
  }

  @media (max-width: 860px) {
    width: 100%;
    height: 160px;
    grid-column: 1 / 3;
    grid-row: 3 / 4;
    img { width: 100%; height: 100%; }
  }
`;
